/**
 * „Klient zobaczy" — what the client answers with, drawn while a question is being written.
 *
 * Owner, 4 November 2026: the preview stands ONLY inside the add / edit card, never under a saved
 * question, and it replaced the phone mock-up the editor dropped in September. It is a stub of the
 * answer, not a rendering of the phone: a scale is its five steps, a number or a text a field of the
 * right height, a choice the options exactly as typed, a date a Polish date's shape, a recording
 * the sentence that says what is asked. The scale draws no end labels — the template carries none.
 */

import type { AuthorableAnswerType } from '../model/editorDraft';
import type { TranslateKey } from '../model/translateKey';
import styles from './EditorForm.module.css';

/** A rating's five steps — the phone's own scale. */
const SCALE = [1, 2, 3, 4, 5] as const;

interface AnswerPreviewProps {
  readonly answerType: AuthorableAnswerType;
  readonly options: readonly string[];
  readonly clipsLeft: number;
  readonly t: TranslateKey;
}

export function AnswerPreview({ answerType, options, clipsLeft, t }: AnswerPreviewProps) {
  const typed = options.map((option) => option.trim()).filter((option) => option !== '');

  return (
    <div className={styles.field}>
      <span className={styles.label}>{t('web:reports.editor.newQuestion.preview')}</span>
      <div className={styles.preview} aria-hidden>
        {answerType === 'Scale' ? (
          <span className={styles.previewScale}>
            {SCALE.map((step) => (
              <span key={step} className={styles.previewStep}>
                {step}
              </span>
            ))}
          </span>
        ) : answerType === 'Number' ? (
          <span className={styles.previewField}>{t('web:reports.editor.newQuestion.previewNumber')}</span>
        ) : answerType === 'ShortText' ? (
          <span className={`${String(styles.previewField)} ${String(styles.previewWide)}`}>
            {t('web:reports.editor.newQuestion.previewShort')}
          </span>
        ) : answerType === 'LongText' ? (
          <span className={`${String(styles.previewField)} ${String(styles.previewWide)} ${String(styles.previewTall)}`}>
            {t('web:reports.editor.newQuestion.previewLong')}
          </span>
        ) : answerType === 'Date' ? (
          <span className={styles.previewField}>{t('web:reports.editor.newQuestion.previewDate')}</span>
        ) : answerType === 'ExerciseClip' ? (
          <span className={styles.previewNote}>{t('web:reports.editor.clip.hint', { left: clipsLeft })}</span>
        ) : typed.length === 0 ? (
          <span className={styles.previewNote}>{t('web:reports.editor.newQuestion.previewNoOptions')}</span>
        ) : (
          <ul className={styles.previewOptions}>
            {typed.map((option, index) => (
              <li
                key={index}
                className={`${String(styles.previewOption)} ${
                  answerType === 'MultiChoice' ? String(styles.previewOptionMulti) : ''
                }`}
              >
                {option}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
